import React, { useRef, useState, useEffect } from 'react';
import { Eraser, Check } from 'lucide-react';

/**
 * SignaturePad - HTML5 Canvas signature capture.
 * Props:
 *  - onSave(base64PngDataUrl, signatoryName) called when user clicks validate
 *  - onCancel()
 *  - initialName
 *  - demanderLeNom : faux quand l'ecran d'a cote demande deja le signataire.
 */
export default function SignaturePad({
  onSave, onCancel, initialName = '', demanderLeNom = true,
}) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [name, setName] = useState(initialName);

  // Quand l'ecran d'a cote demande deja le signataire, le pad ne le redemande
  // pas : il y avait **deux champs pour un seul nom**, dix lignes d'ecart, et
  // c'etait le second — celui du cadre — que « Valider la signature »
  // regardait. On remplissait le premier, on cliquait, et rien ne se passait.
  useEffect(() => {
    if (!demanderLeNom) setName(initialName);
  }, [demanderLeNom, initialName]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Resize for crisp rendering
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1A1B2E';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
    setHasSignature(true);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => setDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const save = () => {
    if (!hasSignature) return;
    if (!name.trim()) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave?.(dataUrl, name.trim());
  };

  return (
    <div className="space-y-3">
      {demanderLeNom && (
        <div>
          <label className="block text-[12px] font-semibold text-[#4A4B63] mb-1">Nom du signataire</label>
          <input
            data-testid="signature-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Prénom Nom"
            className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none"
          />
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[12px] font-semibold text-[#4A4B63]">Signature manuscrite</label>
          <span className="text-[11px] text-[#8E8FA5]">Dessinez dans le cadre ci-dessous</span>
        </div>
        <div className="relative rounded-xl border-2 border-dashed border-[#C7D2FE] bg-[#FAFAFE] h-[180px]">
          <canvas
            ref={canvasRef}
            data-testid="signature-canvas"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
            className="w-full h-full cursor-crosshair rounded-xl"
            style={{ touchAction: 'none' }}
          />
          {!hasSignature && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[12.5px] text-[#B8B9CC] italic">
              Signez ici avec votre souris ou votre doigt
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          data-testid="signature-clear-btn"
          onClick={clear}
          type="button"
          className="h-10 px-3.5 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-medium text-[#4A4B63] flex items-center gap-1.5 hover:border-[#C7D2FE]"
        >
          <Eraser className="w-4 h-4" /> Effacer
        </button>
        <div className="flex-1" />
        {onCancel && (
          <button type="button" onClick={onCancel} className="h-10 px-3.5 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-medium text-[#4A4B63] hover:border-[#C7D2FE]">
            Annuler
          </button>
        )}
        {/* « Valider la signature » reste gris tant qu'il manque le nom ou le
            trace — et ne disait pas lequel : on cliquait, il ne se passait
            rien, et le defaut se lisait comme une panne du logiciel. La phrase
            nomme ce qui manque, la ou le bouton est. */}
        {(!hasSignature || !name.trim()) && (
          <span
            data-testid="signature-manque"
            className="text-[11px] text-[#B45309] text-right leading-tight max-w-[190px]"
          >
            {!name.trim() && !hasSignature
              ? (demanderLeNom
                  ? 'Nom à saisir et signature à dessiner'
                  : 'Signataire à saisir ci-dessus, et signature à dessiner')
              : !name.trim()
                ? (demanderLeNom
                    ? 'Le nom du signataire manque'
                    : 'Le nom du signataire manque, ci-dessus')
                : 'Dessinez votre signature dans le cadre'}
          </span>
        )}
        <button
          data-testid="signature-save-btn"
          onClick={save}
          disabled={!hasSignature || !name.trim()}
          type="button"
          className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-1.5 shadow-sm disabled:opacity-50"
        >
          <Check className="w-4 h-4" /> Valider la signature
        </button>
      </div>
    </div>
  );
}
